import React from "react";
import { Box, Skeleton } from "@mui/material";

// ==============================================
// SKELETON COMPLETO DEL PERFIL PROFESIONAL
// Se muestra mientras isLoading es true en Profesionales.jsx
// Replica la silueta de la página completa
// ==============================================

function SkeletonPerfilCompleto({ isMobile }) {

  // ============================================
  // 1. SECCIÓN PERFIL (avatar + nombre + profesión + rating + certificaciones)
  //    Escenario por defecto: SIN portada, CON foto de perfil
  // ============================================
  const renderPerfilSkeleton = () => {
    if (isMobile) {
      // Mobile: avatar centrado, textos abajo alineados a la izquierda
      return (
        <>
          {/* Avatar squircle centrado */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Skeleton
              animation="wave"
              variant="rounded"
              sx={{ width: 187, height: 187, borderRadius: 4 }}
            />
          </Box>

          {/* Nombre */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "65%", height: 32 }}
          />
          {/* Profesión */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "45%", height: 24 }}
          />
          {/* Rating */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "55%", height: 22, mb: 0.5 }}
          />
          {/* Certificación 1 */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "75%", height: 18 }}
          />
          {/* Certificación 2 */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "70%", height: 18 }}
          />
        </>
      );
    }

    // Desktop: avatar a la izquierda, textos a la derecha alineados abajo
    return (
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, mb: 4 }}>
        {/* Avatar squircle */}
        <Skeleton
          animation="wave"
          variant="rounded"
          sx={{ width: 250, height: 250, borderRadius: 4, flexShrink: 0 }}
        />
        {/* Textos */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Nombre */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "50%", height: 36, mb: 0.5 }}
          />
          {/* Profesión */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "35%", height: 26 }}
          />
          {/* Rating */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "45%", height: 28, mb: 0.5 }}
          />
          {/* Certificación 1 */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "65%", height: 20, mb: 0.5 }}
          />
          {/* Certificación 2 */}
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ width: "60%", height: 20 }}
          />
        </Box>
      </Box>
    );
  };

  // ============================================
  // 2. SECCIÓN SOBRE MÍ
  // ============================================
  const renderSobreMiSkeleton = () => (
    <Box sx={{ mt: 1.5 }}>
      {/* Título "Sobre mí" */}
      <Skeleton
        animation="wave"
        variant="text"
        sx={{ width: "120px", height: 28, mb: 1.5 }}
      />
      {/* Párrafos de descripción */}
      <Skeleton animation="wave" variant="text" sx={{ width: "100%", height: 18 }} />
      <Skeleton animation="wave" variant="text" sx={{ width: "100%", height: 18 }} />
      <Skeleton animation="wave" variant="text" sx={{ width: "95%", height: 18 }} />
      <Skeleton animation="wave" variant="text" sx={{ width: "85%", height: 18 }} />
      <Skeleton animation="wave" variant="text" sx={{ width: "60%", height: 18 }} />
    </Box>
  );

  // ============================================
  // 3. SECCIÓN ESPECIALIDADES (pills)
  // ============================================
  const renderEspecialidadesSkeleton = () => (
    <Box sx={{ mt: 2.5 }}>
      {/* Título "Soy especialista en" */}
      <Skeleton
        animation="wave"
        variant="text"
        sx={{ width: "200px", height: 28, mb: 2 }}
      />
      {/* Pills */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        <Skeleton animation="wave" variant="rounded" sx={{ width: 100, height: 32, borderRadius: 2 }} />
        <Skeleton animation="wave" variant="rounded" sx={{ width: 130, height: 32, borderRadius: 2 }} />
        <Skeleton animation="wave" variant="rounded" sx={{ width: 90, height: 32, borderRadius: 2 }} />
        <Skeleton animation="wave" variant="rounded" sx={{ width: 115, height: 32, borderRadius: 2 }} />
        <Skeleton animation="wave" variant="rounded" sx={{ width: 105, height: 32, borderRadius: 2 }} />
      </Box>
    </Box>
  );

  // ============================================
  // 4. SECCIÓN SERVICIOS (cards)
  // ============================================
  const renderServicioCardSkeleton = () => (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: 3,
        bgcolor: "#ffffff",
        p: 2,
      }}
    >
      {/* Nombre servicio */}
      <Skeleton animation="wave" variant="text" sx={{ width: "60%", height: 22 }} />
      {/* Pill modalidad */}
      <Skeleton animation="wave" variant="rounded" sx={{ width: 120, height: 24, borderRadius: 2, mb: 0.5 }} />
      {/* Duración */}
      <Skeleton animation="wave" variant="text" sx={{ width: "30%", height: 18 }} />
      {/* Precio */}
      <Skeleton animation="wave" variant="text" sx={{ width: "20%", height: 22 }} />
      
      {isMobile ? (
        <>
          {/* Botón agendar full width */}
          <Skeleton animation="wave" variant="rounded" sx={{ width: "100%", height: 42, borderRadius: 2, mt: 1 }} />
          {/* Link descripción centrado */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <Skeleton animation="wave" variant="text" sx={{ width: "55%", height: 16 }} />
          </Box>
        </>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
          {/* Link descripción */}
          <Skeleton animation="wave" variant="text" sx={{ width: "40%", height: 16 }} />
          {/* Botón agendar */}
          <Skeleton animation="wave" variant="rounded" sx={{ width: 100, height: 38, borderRadius: 2 }} />
        </Box>
      )}
    </Box>
  );

  const renderServiciosSkeleton = () => (
    <Box sx={{ mt: 3 }}>
      {/* Título tipo de consulta */}
      <Skeleton
        animation="wave"
        variant="text"
        sx={{ width: "160px", height: 24, mb: 2 }}
      />
      {renderServicioCardSkeleton()}
      {renderServicioCardSkeleton()}
    </Box>
  );

  // ============================================
  // 5. SECCIÓN CONTENIDO (card con imagen)
  // ============================================
  const renderContenidoCardSkeleton = () => (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: 3,
        bgcolor: "#ffffff",
        p: 2,
      }}
    >
      <Box sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "center" : "flex-start",
        gap: isMobile ? 0 : 2,
      }}>
        {/* Imagen */}
        <Skeleton
          animation="wave"
          variant="rounded"
          sx={{
            width: isMobile ? 280 : 300,
            height: isMobile ? 120 : 150,
            borderRadius: 2,
            flexShrink: 0,
            mb: isMobile ? 2 : 0,
          }}
        />
        {/* Info */}
        <Box sx={{
          width: isMobile ? "100%" : "auto",
          flex: isMobile ? "none" : 1,
          display: "flex",
          flexDirection: "column",
          height: isMobile ? "auto" : 150,
        }}>
          {/* Nombre */}
          <Skeleton animation="wave" variant="text" sx={{ width: "70%", height: 22 }} />
          {/* Pill tipo */}
          <Skeleton animation="wave" variant="rounded" sx={{ width: 80, height: 22, borderRadius: 2, mb: 0.5 }} />
          {/* Precio */}
          <Skeleton animation="wave" variant="text" sx={{ width: "25%", height: 22 }} />
          {/* Espacio flexible en desktop */}
          <Box sx={{ flex: isMobile ? 0 : 1 }} />
          {/* Botón */}
          <Box sx={{
            display: "flex",
            justifyContent: isMobile ? "stretch" : "flex-end",
          }}>
            <Skeleton
              animation="wave"
              variant="rounded"
              sx={{
                width: isMobile ? "100%" : 100,
                height: 42,
                borderRadius: 2,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderContenidoSkeleton = () => (
    <Box sx={{ mt: 3 }}>
      {/* Título "Contenido" */}
      <Skeleton
        animation="wave"
        variant="text"
        sx={{ width: "120px", height: 28, mb: 2 }}
      />
      {renderContenidoCardSkeleton()}
    </Box>
  );

  // ============================================
  // 6. SECCIÓN EVENTOS (card con imagen, similar a contenido)
  // ============================================
  const renderEventoCardSkeleton = () => (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: 3,
        bgcolor: "#ffffff",
        p: 2,
      }}
    >
      <Box sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "center" : "flex-start",
        gap: isMobile ? 0 : 2,
      }}>
        {/* Imagen */}
        <Skeleton
          animation="wave"
          variant="rounded"
          sx={{
            width: isMobile ? 280 : 300,
            height: isMobile ? 120 : 150,
            borderRadius: 2,
            flexShrink: 0,
            mb: isMobile ? 2 : 0,
          }}
        />
        {/* Info */}
        <Box sx={{
          width: isMobile ? "100%" : "auto",
          flex: isMobile ? "none" : 1,
          display: "flex",
          flexDirection: "column",
          height: isMobile ? "auto" : 150,
        }}>
          {/* Nombre */}
          <Skeleton animation="wave" variant="text" sx={{ width: "65%", height: 22 }} />
          {/* Pill modalidad */}
          <Skeleton animation="wave" variant="rounded" sx={{ width: 120, height: 22, borderRadius: 2, mb: 0.5 }} />
          {/* Fecha */}
          <Skeleton animation="wave" variant="text" sx={{ width: "80%", height: 18 }} />
          {/* Precio */}
          <Skeleton animation="wave" variant="text" sx={{ width: "20%", height: 22 }} />
          {/* Espacio flexible en desktop */}
          <Box sx={{ flex: isMobile ? 0 : 1 }} />
          {/* Botón */}
          <Box sx={{
            display: "flex",
            justifyContent: isMobile ? "stretch" : "flex-end",
          }}>
            <Skeleton
              animation="wave"
              variant="rounded"
              sx={{
                width: isMobile ? "100%" : 100,
                height: 42,
                borderRadius: 2,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderEventosSkeleton = () => (
    <Box sx={{ mt: 3 }}>
      {/* Título "Eventos" */}
      <Skeleton
        animation="wave"
        variant="text"
        sx={{ width: "100px", height: 28, mb: 2 }}
      />
      {renderEventoCardSkeleton()}
    </Box>
  );

  // ============================================
  // 7. SECCIÓN CONTACTO (pill buttons)
  // ============================================
  const renderContactoSkeleton = () => (
    <Box sx={{ mt: 1.5 }}>
      {/* Título "Encuéntrame en" */}
      <Skeleton
        animation="wave"
        variant="text"
        sx={{ width: "160px", height: 28, mb: 1.5 }}
      />
      <Box sx={{
        display: isMobile ? "grid" : "flex",
        gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "none",
        gap: 1,
        mb: 1.5,
      }}>
        <Skeleton animation="wave" variant="rounded" sx={{ width: isMobile ? "100%" : 195, height: 36, borderRadius: 2 }} />
        <Skeleton animation="wave" variant="rounded" sx={{ width: isMobile ? "100%" : 195, height: 36, borderRadius: 2 }} />
        <Skeleton animation="wave" variant="rounded" sx={{ width: isMobile ? "100%" : 195, height: 36, borderRadius: 2 }} />
        <Skeleton animation="wave" variant="rounded" sx={{ width: isMobile ? "100%" : 195, height: 36, borderRadius: 2 }} />
      </Box>
    </Box>
  );

  // ============================================
  // 8. SECCIÓN EVALUACIONES (rating + cards)
  // ============================================
  const renderEvaluacionesSkeleton = () => (
    <Box sx={{ mt: 4 }}>
      {/* Título "Evaluaciones" */}
      <Skeleton
        animation="wave"
        variant="text"
        sx={{ width: "150px", height: 28, mb: 3 }}
      />
      {/* Promedio */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Skeleton animation="wave" variant="text" sx={{ width: 60, height: 56 }} />
        <Box>
          <Skeleton animation="wave" variant="text" sx={{ width: 120, height: 22 }} />
          <Skeleton animation="wave" variant="text" sx={{ width: 160, height: 16 }} />
        </Box>
      </Box>
      {/* Cards de evaluación */}
      {[1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: "#ffffff",
            boxShadow: 1,
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Skeleton animation="wave" variant="text" sx={{ width: 100, height: 20 }} />
            <Skeleton animation="wave" variant="text" sx={{ width: 80, height: 16 }} />
          </Box>
          <Skeleton animation="wave" variant="text" sx={{ width: "90%", height: 18 }} />
          <Skeleton animation="wave" variant="text" sx={{ width: "40%", height: 16, mt: 1 }} />
        </Box>
      ))}
    </Box>
  );

  // ============================================
  // RENDER PRINCIPAL - Silueta completa de la página
  // ============================================
  return (
    <>
      {renderPerfilSkeleton()}
      {renderSobreMiSkeleton()}
      {renderEspecialidadesSkeleton()}
      {renderServiciosSkeleton()}
      {renderContenidoSkeleton()}
      {renderEventosSkeleton()}
      {renderContactoSkeleton()}
      {renderEvaluacionesSkeleton()}
    </>
  );
}

export default SkeletonPerfilCompleto;
